// Public types ----------------------------------------------------------------

export type WaveformPeaks =
  | Readonly<{ status: 'ready'; peaks: readonly number[] }>
  | Readonly<{ status: 'unavailable' }>

// Constants -------------------------------------------------------------------

const MAX_BARS = 128
const MAX_SAMPLES_PER_BAR = 512
const MAX_CHANNELS = 32

// Private functions -----------------------------------------------------------

// Samples each time bucket evenly, bounding work independently of recording length.
const bucketPeak = (
  channels: readonly Float32Array[],
  start: number,
  end: number
): number => {
  let peak = 0

  const stride = Math.max(1, Math.ceil((end - start) / MAX_SAMPLES_PER_BAR))

  for (const channel of channels) {
    for (let index = start; index < end; index += stride) {
      peak = Math.max(peak, Math.abs(channel[index]))
    }
  }

  return peak
}

// Gives silence a thin baseline without inventing amplitude.
const peakBar = (peak: number, index: number): string => {
  const height = Math.max(1, Math.round(peak * 100))

  return `<rect x="${index * 3}" y="${(100 - height) / 2}" width="2" height="${height}" rx="1"/>`
}

// Public functions ------------------------------------------------------------

// Combines absolute channel peaks so opposite-phase stereo cannot cancel out.
export const extractWaveformPeaks = (
  channels: readonly Float32Array[]
): WaveformPeaks => {
  const length = channels[0]?.length ?? 0
  const valid =
    channels.length > 0 &&
    channels.length <= MAX_CHANNELS &&
    length > 0 &&
    channels.every((channel) => channel.length === length)
  const count = Math.min(MAX_BARS, length)
  const peaks = valid
    ? Array.from({ length: count }, (_, index) =>
        bucketPeak(
          channels,
          Math.floor((index * length) / count),
          Math.floor(((index + 1) * length) / count)
        )
      )
    : []
  const maximum = Math.max(...peaks)
  const finite = peaks.length > 0 && peaks.every(Number.isFinite)

  return finite
    ? {
        status: 'ready',
        peaks: peaks.map((peak) => (maximum > 0 ? peak / maximum : 0))
      }
    : { status: 'unavailable' }
}

// Encodes sample-derived geometry for Media Chrome's exposed track mask.
export const waveformMask = (peaks: readonly number[]): string => {
  const bars = peaks.map(peakBar).join('')
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${peaks.length * 3} 100" preserveAspectRatio="none">${bars}</svg>`

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}
